import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  FileText,
  Download,
  Eye,
  Calendar,
  DollarSign,
  Filter,
  Receipt,
  ScrollText,
  Loader2,
  AlertCircle,
  Zap,
  Plus,
} from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import {
  GET_INVOICES_BY_ORGANIZATION,
  GET_STATEMENTS_BY_ORGANIZATION,
  type InvoiceStatus,
  type StatementStatus,
  type GetInvoicesByOrganizationResponse,
  type GetStatementsByOrganizationResponse,
} from '@/graphql/invoices';
import { CreateInvoiceModal } from '@/components/modals/CreateInvoiceModal';
import { InvoiceDetailModal } from '@/components/modals/InvoiceDetailModal';
import { RecordPaymentModal } from '@/components/modals/RecordPaymentModal';
import { StatementDetailModal } from '@/components/modals/StatementDetailModal';
import { GenerateStatementModal } from '@/components/modals/GenerateStatementModal';

// ==================== Status Display Config ====================

const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
  SENT: 'Sent',
  PAID: 'Paid',
  PARTIAL: 'Partial',
  DISPUTED: 'Disputed',
  VOID: 'Void',
};

const INVOICE_STATUS_STYLES: Record<InvoiceStatus, string> = {
  DRAFT: 'bg-pale-gray-2 text-chalkboard dark:bg-night-pond dark:text-rain-cloud',
  APPROVED: 'bg-pond/10 text-pond dark:bg-pond-light/20 dark:text-pond-light',
  SENT: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light',
  PAID: 'bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua',
  PARTIAL: 'bg-yellow/10 text-earth dark:bg-yellow-light/20 dark:text-yellow-light',
  DISPUTED: 'bg-rust/10 text-rust dark:bg-salmon/20 dark:text-salmon',
  VOID: 'bg-pale-gray-2 text-chalkboard/50 dark:bg-night-pond dark:text-rain-cloud/50',
};

const STATEMENT_STATUS_LABELS: Record<StatementStatus, string> = {
  DRAFT: 'Draft',
  FINALIZED: 'Finalized',
  SENT: 'Sent',
  PAID: 'Paid',
  PARTIAL: 'Partial',
  VOID: 'Void',
};

const STATEMENT_STATUS_STYLES: Record<StatementStatus, string> = {
  DRAFT: 'bg-pale-gray-2 text-chalkboard dark:bg-night-pond dark:text-rain-cloud',
  FINALIZED: 'bg-pond/10 text-pond dark:bg-pond-light/20 dark:text-pond-light',
  SENT: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light',
  PAID: 'bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua',
  PARTIAL: 'bg-yellow/10 text-earth dark:bg-yellow-light/20 dark:text-yellow-light',
  VOID: 'bg-pale-gray-2 text-chalkboard/50 dark:bg-night-pond dark:text-rain-cloud/50',
};

const SOURCE_LABELS: Record<string, { label: string; icon?: typeof Zap }> = {
  MANUAL: { label: 'Manual' },
  BLAST: { label: 'BLAST', icon: Zap },
  RECURRING: { label: 'Recurring' },
};

// ==================== Component ====================

interface InvoicesStatementsTabProps {
  clientId: string;
}

export function InvoicesStatementsTab({ clientId }: InvoicesStatementsTabProps) {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeSection, setActiveSection] = useState<'invoices' | 'statements'>('invoices');

  // Modal state
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showGenerateStatement, setShowGenerateStatement] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [selectedStatementId, setSelectedStatementId] = useState<string | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<{
    invoiceId: string;
    invoiceNumber: string;
    balanceDue: number;
  } | null>(null);

  // Fetch invoices
  const {
    data: invoicesData,
    loading: invoicesLoading,
    error: invoicesError,
  } = useQuery<GetInvoicesByOrganizationResponse>(GET_INVOICES_BY_ORGANIZATION, {
    variables: { organizationId: clientId },
    skip: !clientId,
  });

  // Fetch statements
  const {
    data: statementsData,
    loading: statementsLoading,
    error: statementsError,
  } = useQuery<GetStatementsByOrganizationResponse>(GET_STATEMENTS_BY_ORGANIZATION, {
    variables: { organizationId: clientId },
    skip: !clientId,
  });

  const invoices = useMemo(() => invoicesData?.invoicesByOrganization ?? [], [invoicesData?.invoicesByOrganization]);
  const statements = statementsData?.statementsByOrganization ?? [];

  // Filter invoices
  const filteredInvoices = invoices.filter((inv) => {
    return statusFilter === 'all' || inv.status === statusFilter;
  });

  // Calculate summary stats
  const totalBilled = invoices.reduce((sum, inv) => sum + inv.total, 0);
  const totalPaid = invoices
    .filter((inv) => inv.status === 'PAID')
    .reduce((sum, inv) => sum + inv.total, 0);
  const totalOutstanding = invoices
    .filter((inv) => ['APPROVED', 'SENT', 'PARTIAL', 'DISPUTED'].includes(inv.status))
    .reduce((sum, inv) => sum + inv.balanceDue, 0);
  const overdueCount = invoices.filter((inv) => {
    if (inv.status === 'PAID' || inv.status === 'VOID' || inv.status === 'DRAFT') return false;
    return new Date(inv.dueDate) < new Date();
  }).length;

  const isLoading = invoicesLoading || statementsLoading;

  // ==================== Modal Handlers ====================

  const handleViewInvoice = useCallback((invoiceId: string) => {
    setSelectedInvoiceId(invoiceId);
  }, []);

  const handleViewStatement = useCallback((statementId: string) => {
    setSelectedStatementId(statementId);
  }, []);

  const handleRecordPaymentFromDetail = useCallback((invoiceId: string) => {
    // Close the detail modal and open payment modal
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    if (invoice) {
      setSelectedInvoiceId(null);
      // Use setTimeout to let the detail modal close first
      setTimeout(() => {
        setPaymentTarget({
          invoiceId: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          balanceDue: invoice.balanceDue,
        });
      }, 150);
    }
  }, [invoices]);

  const handleViewInvoiceFromStatement = useCallback((invoiceId: string) => {
    // Close statement detail and open invoice detail
    setSelectedStatementId(null);
    setTimeout(() => {
      setSelectedInvoiceId(invoiceId);
    }, 150);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate dark:text-mist">Invoices & Statements</h2>
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            Client invoices and billing statements
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-pond dark:text-pond-light" />
              </div>
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Total Billed</p>
                <p className="text-xl font-semibold text-slate dark:text-mist">
                  {isLoading ? '—' : formatCurrency(totalBilled)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-turquoise/10 dark:bg-aqua/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-turquoise dark:text-aqua" />
              </div>
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Total Paid</p>
                <p className="text-xl font-semibold text-turquoise dark:text-aqua">
                  {isLoading ? '—' : formatCurrency(totalPaid)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow/10 dark:bg-yellow-light/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-earth dark:text-yellow-light" />
              </div>
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Outstanding</p>
                <p className="text-xl font-semibold text-earth dark:text-yellow-light">
                  {isLoading ? '—' : formatCurrency(totalOutstanding)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                overdueCount > 0 ? 'bg-rust/10 dark:bg-salmon/20' : 'bg-pale-gray-2 dark:bg-night-pond'
              )}>
                <Calendar className={cn(
                  'w-5 h-5',
                  overdueCount > 0 ? 'text-rust dark:text-salmon' : 'text-chalkboard dark:text-rain-cloud'
                )} />
              </div>
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Past Due</p>
                <p className={cn(
                  'text-xl font-semibold',
                  overdueCount > 0 ? 'text-rust dark:text-salmon' : 'text-slate dark:text-mist'
                )}>
                  {isLoading ? '—' : overdueCount}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Toggle */}
      <div className="flex items-center gap-4 border-b border-gray dark:border-night-pond">
        <button
          onClick={() => setActiveSection('invoices')}
          className={cn(
            'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
            activeSection === 'invoices'
              ? 'text-pond dark:text-pond-light border-pond dark:border-pond-light'
              : 'text-chalkboard dark:text-rain-cloud border-transparent hover:text-slate dark:hover:text-mist'
          )}
        >
          <Receipt className="w-4 h-4" />
          Invoices ({invoices.length})
        </button>
        <button
          onClick={() => setActiveSection('statements')}
          className={cn(
            'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
            activeSection === 'statements'
              ? 'text-pond dark:text-pond-light border-pond dark:border-pond-light'
              : 'text-chalkboard dark:text-rain-cloud border-transparent hover:text-slate dark:hover:text-mist'
          )}
        >
          <ScrollText className="w-4 h-4" />
          Statements ({statements.length})
        </button>
      </div>

      {/* Error State */}
      {(invoicesError || statementsError) && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-3 text-rust dark:text-salmon">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">
                Failed to load data: {invoicesError?.message || statementsError?.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-pond dark:text-pond-light" />
          <span className="ml-2 text-sm text-chalkboard dark:text-rain-cloud">Loading...</span>
        </div>
      )}

      {/* Invoices Section */}
      {!isLoading && activeSection === 'invoices' && (
        <div className="space-y-4">
          {/* Filters + Create Button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="select py-1.5 text-sm w-36"
              >
                <option value="all">All Statuses</option>
                {Object.entries(INVOICE_STATUS_LABELS).map(([status, label]) => (
                  <option key={status} value={status}>{label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setShowCreateInvoice(true)}
              className="btn-primary px-3 py-1.5 text-sm flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Create Invoice
            </button>
          </div>

          {/* Invoices Table */}
          <div className="card overflow-hidden">
            <table className="table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th>Due Date</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Balance Due</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th className="w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => {
                  const isOverdue = !['PAID', 'VOID', 'DRAFT'].includes(invoice.status)
                    && new Date(invoice.dueDate) < new Date();
                  const sourceInfo = SOURCE_LABELS[invoice.source] || { label: invoice.source };

                  return (
                    <tr
                      key={invoice.id}
                      className="group cursor-pointer"
                      onClick={() => handleViewInvoice(invoice.id)}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
                            <FileText className="w-4 h-4 text-pond dark:text-pond-light" />
                          </div>
                          <div>
                            <span className="font-medium text-slate dark:text-mist">
                              {invoice.invoiceNumber}
                            </span>
                            {invoice.externalRef && (
                              <p className="text-xs text-chalkboard/60 dark:text-rain-cloud/60">
                                {invoice.externalRef}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="text-chalkboard dark:text-rain-cloud">
                        {formatDate(new Date(invoice.invoiceDate))}
                      </td>
                      <td className={cn(
                        isOverdue && 'text-rust dark:text-salmon font-medium'
                      )}>
                        {formatDate(new Date(invoice.dueDate))}
                        {isOverdue && (
                          <span className="ml-1 text-xs text-rust dark:text-salmon">⚠</span>
                        )}
                      </td>
                      <td className="text-right font-medium text-slate dark:text-mist">
                        {formatCurrency(invoice.total)}
                      </td>
                      <td className={cn(
                        'text-right font-medium',
                        invoice.balanceDue > 0
                          ? 'text-earth dark:text-yellow-light'
                          : 'text-turquoise dark:text-aqua'
                      )}>
                        {formatCurrency(invoice.balanceDue)}
                      </td>
                      <td>
                        <div className="flex items-center gap-1 text-xs text-chalkboard dark:text-rain-cloud">
                          {sourceInfo.icon && <sourceInfo.icon className="w-3 h-3" />}
                          {sourceInfo.label}
                        </div>
                      </td>
                      <td>
                        <span className={cn('badge', INVOICE_STATUS_STYLES[invoice.status])}>
                          {INVOICE_STATUS_LABELS[invoice.status]}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            className="btn-ghost btn-icon btn-sm"
                            title="View invoice"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewInvoice(invoice.id);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            className="btn-ghost btn-icon btn-sm"
                            title="Download PDF"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredInvoices.length === 0 && !isLoading && (
              <div className="p-8 text-center text-chalkboard dark:text-rain-cloud">
                <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>
                  {invoices.length === 0
                    ? 'No invoices yet for this client.'
                    : 'No invoices found matching your filters.'}
                </p>
                {invoices.length === 0 && (
                  <button
                    onClick={() => setShowCreateInvoice(true)}
                    className="mt-3 btn-ghost text-sm px-3 py-1.5 text-pond dark:text-pond-light border border-pond/20 dark:border-pond-light/20"
                  >
                    <Plus className="w-4 h-4 mr-1.5 inline" />
                    Create First Invoice
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Statements Section */}
      {!isLoading && activeSection === 'statements' && (
        <div className="space-y-4">
          {/* Generate Button */}
          <div className="flex items-center justify-end">
            <button
              onClick={() => setShowGenerateStatement(true)}
              className="btn-primary px-3 py-1.5 text-sm flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Generate Statement
            </button>
          </div>

          {/* Statements Table */}
          <div className="card overflow-hidden">
            <table className="table">
              <thead>
                <tr>
                  <th>Statement #</th>
                  <th>Billing Period</th>
                  <th>Statement Date</th>
                  <th>Due Date</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Balance Due</th>
                  <th>Status</th>
                  <th className="w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {statements.map((statement) => (
                  <tr
                    key={statement.id}
                    className="group cursor-pointer"
                    onClick={() => handleViewStatement(statement.id)}
                  >
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-violet/10 dark:bg-violet-light/20 flex items-center justify-center">
                          <ScrollText className="w-4 h-4 text-violet dark:text-violet-light" />
                        </div>
                        <span className="font-medium text-slate dark:text-mist">
                          {statement.statementNumber}
                        </span>
                      </div>
                    </td>
                    <td className="text-chalkboard dark:text-rain-cloud">
                      {formatDate(new Date(statement.billingPeriodStart))} – {formatDate(new Date(statement.billingPeriodEnd))}
                    </td>
                    <td className="text-chalkboard dark:text-rain-cloud">
                      {formatDate(new Date(statement.statementDate))}
                    </td>
                    <td className="text-chalkboard dark:text-rain-cloud">
                      {formatDate(new Date(statement.dueDate))}
                    </td>
                    <td className="text-right font-medium text-slate dark:text-mist">
                      {formatCurrency(statement.totalAmount)}
                    </td>
                    <td className={cn(
                      'text-right font-medium',
                      statement.balanceDue > 0
                        ? 'text-rust dark:text-salmon'
                        : 'text-turquoise dark:text-aqua'
                    )}>
                      {formatCurrency(statement.balanceDue)}
                    </td>
                    <td>
                      <span className={cn('badge', STATEMENT_STATUS_STYLES[statement.status])}>
                        {STATEMENT_STATUS_LABELS[statement.status]}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="btn-ghost btn-icon btn-sm"
                          title="View statement"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewStatement(statement.id);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          className="btn-ghost btn-icon btn-sm"
                          title="Download PDF"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {statements.length === 0 && !isLoading && (
              <div className="p-8 text-center text-chalkboard dark:text-rain-cloud">
                <ScrollText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No statements yet for this client.</p>
                <button
                  onClick={() => setShowGenerateStatement(true)}
                  className="mt-3 btn-ghost text-sm px-3 py-1.5 text-pond dark:text-pond-light border border-pond/20 dark:border-pond-light/20"
                >
                  <Plus className="w-4 h-4 mr-1.5 inline" />
                  Generate First Statement
                </button>
              </div>
            )}
          </div>

          {/* Statement Info */}
          <div className="card">
            <div className="card-body">
              <p className="text-sm text-chalkboard dark:text-rain-cloud">
                <strong className="text-slate dark:text-mist">Statements</strong> group approved invoices
                for a billing period. They provide an overview and itemized report of all invoices on the
                statement, used for billing the client on a schedule.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ==================== Modals ==================== */}

      {/* Create Invoice Modal (#30) */}
      <CreateInvoiceModal
        isOpen={showCreateInvoice}
        onClose={() => setShowCreateInvoice(false)}
        clientId={clientId}
      />

      {/* Invoice Detail Modal (#31) */}
      <InvoiceDetailModal
        isOpen={selectedInvoiceId !== null}
        onClose={() => setSelectedInvoiceId(null)}
        invoiceId={selectedInvoiceId}
        clientId={clientId}
        onRecordPayment={handleRecordPaymentFromDetail}
      />

      {/* Record Payment Modal (#33) */}
      <RecordPaymentModal
        isOpen={paymentTarget !== null}
        onClose={() => setPaymentTarget(null)}
        invoiceId={paymentTarget?.invoiceId ?? null}
        invoiceNumber={paymentTarget?.invoiceNumber ?? ''}
        balanceDue={paymentTarget?.balanceDue ?? 0}
        clientId={clientId}
      />

      {/* Statement Detail Modal (#32) */}
      <StatementDetailModal
        isOpen={selectedStatementId !== null}
        onClose={() => setSelectedStatementId(null)}
        statementId={selectedStatementId}
        clientId={clientId}
        onViewInvoice={handleViewInvoiceFromStatement}
      />

      {/* Generate Statement Modal (#32) */}
      <GenerateStatementModal
        isOpen={showGenerateStatement}
        onClose={() => setShowGenerateStatement(false)}
        clientId={clientId}
      />
    </div>
  );
}
